import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Subject, firstValueFrom } from 'rxjs';
import { EchoService } from './echo.service';
import { environment } from '../../environments/environment';

export interface IncomingCall {
  invitation_id: number;
  room_name: string;
  caller: { id: number; name: string };
}
export interface OutgoingCall { room: string; status: 'calling' | 'declined' | 'missed'; }

@Injectable({ providedIn: 'root' })
export class CallService {
  incoming$ = new Subject<IncomingCall | null>();
  outgoing$ = new BehaviorSubject<OutgoingCall | null>(null);
  response$ = new Subject<{ type: 'accepted' | 'declined'; data: any }>();

  private listening = false;
  private ringTimer: any;
  private pendingRoom: string | null = null;
  private pendingCount = 0;
  private declined = 0;

  constructor(private http: HttpClient, private echoService: EchoService, private router: Router) {}

  listen() {
    const userId = localStorage.getItem('user_id');
    if (this.listening || !userId) return;
    this.listening = true;
    this.echoService.connect().private('user.' + userId)
      .listen('.call.incoming', (e: IncomingCall) => this.incoming$.next(e))
      .listen('.call.cancelled', () => this.incoming$.next(null))
      .listen('.call.accepted', (e: any) => this.onAccepted(e))
      .listen('.call.declined', (e: any) => this.onDeclined(e));
  }

  stop() { this.listening = false; this.finish(); }

  async invite(calleeIds: number[], room?: string) {
    if (!room && this.pendingRoom) return;          // pehle se call ja rahi hai
    const res: any = await firstValueFrom(
      this.http.post(`${environment.apiUrl}calls/invite`,
        { callee_ids: calleeIds, room_name: room }, { headers: this.echoService.headers() })
    );
    if (room) return res;                            // chalti call mein log add kiye

    this.pendingRoom = res.room_name;
    this.pendingCount = calleeIds.length;
    this.declined = 0;
    this.outgoing$.next({ room: res.room_name, status: 'calling' });
    this.ringTimer = setTimeout(() => this.noAnswer(), 30000);
    return res;
  }

  cancelOutgoing() {
    if (!this.pendingRoom) return;
    this.cancel(this.pendingRoom);
    this.finish();
  }

  respond(invitationId: number, action: 'accept' | 'decline'): Promise<any> {
    return firstValueFrom(
      this.http.post(`${environment.apiUrl}calls/${invitationId}/respond`, { action }, { headers: this.echoService.headers() })
    );
  }

  cancel(room: string) {
    this.http.post(`${environment.apiUrl}calls/${room}/cancel`, {}, { headers: this.echoService.headers() })
      .subscribe({ error: () => {} });
  }

  private onAccepted(e: any) {
    this.response$.next({ type: 'accepted', data: e });
    if (e.room_name !== this.pendingRoom) return;
    const room = this.pendingRoom;
    this.finish();
    this.router.navigate(['/video-call', room]);
  }

  private onDeclined(e: any) {
    this.response$.next({ type: 'declined', data: e });
    if (e.room_name !== this.pendingRoom) return;
    if (++this.declined >= this.pendingCount) this.showEnd('declined');
  }

  private noAnswer() {
    if (!this.pendingRoom) return;
    this.cancel(this.pendingRoom);                   // server par 'missed' + callee ka popup band
    this.showEnd('missed');
  }

  private showEnd(status: 'declined' | 'missed') {
    clearTimeout(this.ringTimer);
    this.pendingRoom = null;
    this.outgoing$.next({ room: '', status });
    setTimeout(() => { if (this.outgoing$.value?.status !== 'calling') this.outgoing$.next(null); }, 2500);
  }

  private finish() {
    clearTimeout(this.ringTimer);
    this.pendingRoom = null;
    this.outgoing$.next(null);
  }
}